import { Injectable } from '@angular/core';
import { BaseService } from '../base.service';
import { BlueprintStore } from '../stores/blueprint.store';
import { ExternalLinkStore } from '../stores/external-link.store';
import { IntelliCatchReachabilityStore } from '../stores/intellicatch-reachability.store';
import { ProjectStore } from '../stores/projects.store';
import { WorkspaceStore } from '../stores/workspace.store';
import { PermissionService } from '../permission.service';
import { IBluePrintModel } from '../../models/blueprint/blueprint.model';
import { IBluePrintNodeModel } from '../../models/blueprint/blueprint-node.model';
import { IFieldModel } from '../../models/blueprint/blueprint-field.model';
import { IExternalLink } from '../../models/child-application/externallink.model';
import { isAiProviderUsable } from '../../models/child-application/ai-provider.enum';
import { IIntelliCatchAvailability, IIntelliCatchTestFieldIds, IntelliCatchAvailabilityState } from '../../models/copilot/intellicatch-availability.enum';
import { IntelliCatchUrlReachability } from '../../models/copilot/intellicatch-reachability.enum';
import * as BluePrintConstants from '../../modules/catch-core/data/constants/blueprint.constants';
import * as UiMessageConstants from '../../modules/catch-core/data/constants/ui-message.constants';
import { findIntelliCatchLink } from '../../modules/catch-core/data/constants/app.constants';
import { RELATIONSHIP_TYPE_PARENT_CHILD } from '../../modules/catch-core/data/constants/relationship-type.constants';
import { NODE_UPDATE } from '../../modules/catch-core/data/constants/permission.constants';

/**
 * The single place that decides whether the IntelliCatch "Generate Tests" action is offered for an
 * item type, and when it is not, why. Every surface that offers the action asks here rather than
 * re-deriving the rule.
 */
@Injectable({
  providedIn: 'root',
})
export class IntelliCatchAvailabilityHelper extends BaseService {
  private intelliCatchLink: IExternalLink | null = null;

  constructor(
    private externalLinkStore: ExternalLinkStore,
    private blueprintStore: BlueprintStore,
    private workspaceStore: WorkspaceStore,
    private projectStore: ProjectStore,
    private permissionService: PermissionService,
    private intelliCatchReachabilityStore: IntelliCatchReachabilityStore,
  ) {
    super();

    this.subscribers.intelliCatchExternalLink = this.externalLinkStore.$firstExternalLink.subscribe((externalLink) => {
      this.intelliCatchLink = findIntelliCatchLink(externalLink?.externalLinks);
    });
  }

  /**
   * @param nodeTypeId the item type the action would be offered on. Blank means there is no parent
   * item (the header chat creates a Test on its own), so the parent-child rule does not apply.
   *
   * A template that cannot hold a Test under this item type is not applicable rather than blocked:
   * nothing the user or an administrator does on this item will ever make it possible, and the "+"
   * button already hides itself on the same reasoning. Blocked is kept for the conditions someone
   * can act on - IntelliCatch configuration, the Test item type's fields, and permission.
   */
  public getGenerateTestsAvailability(nodeTypeId: string | null | undefined): IIntelliCatchAvailability {
    const blueprint = this.getBlueprint();
    const testNodeType = this.getTestNodeType(blueprint);
    if (!testNodeType) {
      return this.notApplicable();
    }

    if (!this.isTestAllowedUnder(blueprint, nodeTypeId, testNodeType.id)) {
      return this.notApplicable();
    }

    const intelliCatchReason = this.getIntelliCatchBlockedReason();
    if (intelliCatchReason) {
      return this.blocked(intelliCatchReason);
    }

    const missingFieldReason = this.getMissingTestFieldReason(blueprint, testNodeType);
    if (missingFieldReason) {
      return this.blocked(missingFieldReason);
    }

    // Not node:create - the server creates both the Test and its parent-child link through
    // PatchNode/PatchEdge, which are gated on node:update. node:create gates CloneNode only, so
    // asking for it here would disable the action for a role the API would have let through.
    if (!this.permissionService.hasPermission(NODE_UPDATE, this.projectStore?.selectedProject?.id)) {
      return this.blocked(UiMessageConstants.INTELLICATCH_NO_CREATE_PERMISSION);
    }

    return { state: IntelliCatchAvailabilityState.Available, reason: '' };
  }

  /**
   * Ids of the fields IntelliCatch fills, resolved from the Test item type's own assigned fields.
   * Null when the template has no Test item type.
   */
  public getTestFieldIds(): IIntelliCatchTestFieldIds | null {
    const blueprint = this.getBlueprint();
    const testNodeType = this.getTestNodeType(blueprint);
    if (!testNodeType) {
      return null;
    }

    const assignedFields = this.getAssignedFields(blueprint, testNodeType);
    return {
      stepsTableFieldId: this.findStepsTableField(assignedFields)?.id ?? '',
      assigneeFieldId: this.findFieldByName(assignedFields, BluePrintConstants.ASSIGNED_TO_FIELD)?.id ?? '',
      sourceFieldId: this.findFieldByName(assignedFields, BluePrintConstants.SOURCE_FIELD)?.id ?? '',
    };
  }

  private notApplicable(): IIntelliCatchAvailability {
    return { state: IntelliCatchAvailabilityState.NotApplicable, reason: '' };
  }

  private blocked(reason: string): IIntelliCatchAvailability {
    return { state: IntelliCatchAvailabilityState.Blocked, reason: reason ?? '' };
  }

  private getBlueprint(): IBluePrintModel | null {
    const bluePrintId = this.workspaceStore?.selectedWorkspaceInstance?.bluePrintId;
    return this.blueprintStore?.getBlueprintByIdFromCache(bluePrintId) ?? this.blueprintStore?.selectedBluePrint ?? null;
  }

  private getTestNodeType(blueprint: IBluePrintModel | null): IBluePrintNodeModel | null {
    if (!blueprint || !Array.isArray(blueprint.nodes)) {
      return null;
    }
    return blueprint.nodes.find((node) => node?.name?.toLowerCase() === BluePrintConstants.TEST_NODE_LOWER) ?? null;
  }

  private getIntelliCatchBlockedReason(): string {
    const link = this.intelliCatchLink;
    if (!link || !isAiProviderUsable(link.attributes)) {
      return UiMessageConstants.INTELLICATCH_NOT_CONFIGURED;
    }
    if (link.active !== true) {
      return UiMessageConstants.INTELLICATCH_DISABLED;
    }
    if (!link.url) {
      return UiMessageConstants.INTELLICATCH_URL_NOT_ACCESSIBLE;
    }
    return this.getUrlReachabilityReason();
  }

  // A configured URL that nothing answers on is as unusable as no URL at all - the header chat
  // button has always refused it, and offering Generate Tests would open a chat against nothing.
  // Until the shared probe has answered the action stays blocked rather than optimistically
  // available, because a button that enables and then fails is worse than one that waits.
  private getUrlReachabilityReason(): string {
    const reachability = this.intelliCatchReachabilityStore?.reachability ?? IntelliCatchUrlReachability.Unknown;
    if (reachability === IntelliCatchUrlReachability.Reachable) {
      return '';
    }
    if (reachability === IntelliCatchUrlReachability.Unreachable) {
      return UiMessageConstants.INTELLICATCH_URL_NOT_ACCESSIBLE;
    }
    return UiMessageConstants.INTELLICATCH_URL_CHECKING;
  }

  // Only parent-child edges grant containment; an association between the two types does not let a
  // Test be created under the parent. Every source in every edge's mapping is considered, not the
  // first one.
  private isTestAllowedUnder(blueprint: IBluePrintModel | null, parentNodeTypeId: string | null | undefined, testNodeTypeId: string): boolean {
    if (!parentNodeTypeId) {
      return true;
    }
    if (!blueprint || !Array.isArray(blueprint.edges) || !testNodeTypeId) {
      return false;
    }

    return blueprint.edges.some((edge) => {
      if (!edge || typeof edge.relationshipType !== 'string' || edge.relationshipType.toLowerCase() !== RELATIONSHIP_TYPE_PARENT_CHILD) {
        return false;
      }
      const mapping = edge.sourceTargetMapping;
      if (!mapping || typeof mapping !== 'object') {
        return false;
      }
      return Object.entries(mapping).some(([sourceId, targetIds]) => sourceId === parentNodeTypeId && Array.isArray(targetIds) && targetIds.includes(testNodeTypeId));
    });
  }

  private getMissingTestFieldReason(blueprint: IBluePrintModel | null, testNodeType: IBluePrintNodeModel | null): string {
    const assignedFields = this.getAssignedFields(blueprint, testNodeType);

    if (!this.findStepsTableField(assignedFields)) {
      return this.format(UiMessageConstants.INTELLICATCH_TEST_MISSING_FIELD, this.getStepsTableFieldName(blueprint));
    }
    if (!this.findFieldByName(assignedFields, BluePrintConstants.ASSIGNED_TO_FIELD)) {
      return this.format(UiMessageConstants.INTELLICATCH_TEST_MISSING_FIELD, BluePrintConstants.FIELD_NAME_ASSIGNEE_TITLE);
    }
    if (!this.findFieldByName(assignedFields, BluePrintConstants.SOURCE_FIELD)) {
      return this.format(UiMessageConstants.INTELLICATCH_TEST_MISSING_FIELD, BluePrintConstants.FIELD_NAME_SOURCE_TITLE);
    }
    return '';
  }

  // A node type's `fields` is keyed by the ids of the template fields assigned to it; the
  // definitions live in the flat blueprint field list. Resolving by name across the whole template
  // would match a field that is not on a Test at all.
  private getAssignedFields(blueprint: IBluePrintModel | null, nodeType: IBluePrintNodeModel | null): IFieldModel[] {
    const assignedFieldIds = nodeType?.fields && typeof nodeType.fields === 'object' ? Object.keys(nodeType.fields) : [];
    const blueprintFields = blueprint && Array.isArray(blueprint.fields) ? blueprint.fields : [];
    if (assignedFieldIds.length === 0 || blueprintFields.length === 0) {
      return [];
    }
    return blueprintFields.filter((field) => !!field?.id && assignedFieldIds.includes(field.id));
  }

  // Named as the template names it when the template has the table but does not assign it to Test.
  private getStepsTableFieldName(blueprint: IBluePrintModel | null): string {
    const tableFields = (blueprint && Array.isArray(blueprint.fields) ? blueprint.fields : []).filter(
      (field) => (field?.fieldType ?? '').toLowerCase() === BluePrintConstants.BLUEPRINT_FIELD_TYPE_TABLE_LOWER,
    );
    const field = this.findFieldByName(tableFields, BluePrintConstants.FIELD_TESTING_TABLE_LOWER) ?? this.findFieldByName(tableFields, BluePrintConstants.TEST_STEPS_FIELD);
    const name = (field?.name ?? '').trim().replace(/:$/, '');
    return name || BluePrintConstants.FIELD_TESTING_TABLE;
  }

  private findStepsTableField(fields: IFieldModel[]): IFieldModel | null {
    if (!Array.isArray(fields)) {
      return null;
    }
    const tableFields = fields.filter((field) => (field?.fieldType ?? '').toLowerCase() === BluePrintConstants.BLUEPRINT_FIELD_TYPE_TABLE_LOWER);
    if (tableFields.length === 0) {
      return null;
    }
    return this.findFieldByName(tableFields, BluePrintConstants.TEST_STEPS_FIELD) ?? tableFields[0];
  }

  private findFieldByName(fields: IFieldModel[], name: string): IFieldModel | null {
    if (!Array.isArray(fields) || !name) {
      return null;
    }
    const target = name.toLowerCase().trim();
    return fields.find((field) => {
      const fieldName = (field?.name ?? '').toLowerCase().trim();
      return fieldName === target || fieldName === `${target}:`;
    }) ?? null;
  }

  private format(message: string, value: string): string {
    if (!message) {
      return '';
    }
    return message.split(UiMessageConstants.MESSAGE_PLACEHOLDER).join(value ?? '');
  }
}
